import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Account, User } from '@adjust/dashboard-core/api/types';
import { hasNewFeatureScope } from '@adjust/dashboard-core/lib/utils/account';

import {
  PaymentMethodData,
  HistoryEvent,
  CurrencyInfo,
  DisplayRolesCount,
  PartnerAdAccountsCount,
  CurrentCharge,
  Invoice,
} from '@api/types';
import AccountDetailsTile from './AccountDetailsTile/AccountDetailsTile';
import { AccountHistoryTile } from './AccountHistoryTile/AccountHistoryTile';
import CreditCardTile from './CreditCardTile/CreditCardTile';
import InvoicesTile from './InvoicesTile/InvoicesTile';
import { PartnerAdAccountsTile } from './PartnerAdAccountsTile/PartnerAdAccountsTile';
import PricingPlanTile from './PricingPlanTile/PricingPlanTile';
import NewPricingPlanTile from './PricingPlanTile/NewPricingPlanTile';
import SecurityConfigurationTile from './SecurityConfigurationTile/SecurityConfigurationTile';
import UserDetailsTile from './UserDetailsTile/UserDetailsTile';
import UsersTile from './UsersTile/UsersTile';

import './AccountDataPage.scss';

export interface AccountDataPageDispatchProps {
  fetchAccountData: () => void;
  fetchUsers: () => void;
  fetchPartnerAdAccountsCounts: () => void;
  fetchInvoces: ({ invoicesLimit }: { invoicesLimit: number }) => void;
}

export interface AccountDataPageStateProps {
  currency: CurrencyInfo;
  events: HistoryEvent[];
  rolesCount: DisplayRolesCount;
  partnerAdAccountsCount: PartnerAdAccountsCount;
  totalUsers: number;
  accountDatetime: Nullable<string>;
  userDatetime: Nullable<string>;
  currentAccount: Nullable<Account>;
  currentUser: Nullable<User>;
  currentCharge: Nullable<CurrentCharge>;
  invoices: Nullable<Invoice[]>;
  paymentMethod: Nullable<PaymentMethodData>;
  twoFactorConfigurationEnabled: boolean;
  hasSecurityPackage: boolean;
  isChargeDataLoading: boolean;
  isLoadingPaymentMethod: boolean;
  isEventsLoading: boolean;
  isUsersLoading: boolean;
  isPartnerAdAccountsLoading: boolean;
  isTwoFactorLoading: boolean;
}

export interface AccountDataPageOwnProps {
  hrefList: Record<string, string>;
}

type AccountDataPageProps = AccountDataPageOwnProps & AccountDataPageStateProps & AccountDataPageDispatchProps;

export const AccountDataPage: React.FC<AccountDataPageProps> = ({
  fetchAccountData,
  currentAccount,
  currentUser,
  currentCharge,
  isLoadingPaymentMethod,
  paymentMethod,
  isChargeDataLoading,
  invoices,
  fetchInvoces,
  hrefList,
  events,
  isEventsLoading,
  twoFactorConfigurationEnabled,
  currency,
  hasSecurityPackage,
  fetchUsers,
  rolesCount,
  totalUsers,
  isUsersLoading,
  fetchPartnerAdAccountsCounts,
  isPartnerAdAccountsLoading,
  partnerAdAccountsCount,
  accountDatetime,
  userDatetime,
  isTwoFactorLoading,
}) => {
  useEffect(() => {
    fetchAccountData();
    fetchUsers();
    fetchPartnerAdAccountsCounts();
    fetchInvoces({ invoicesLimit: 4 });
  }, [fetchAccountData, fetchUsers, fetchPartnerAdAccountsCounts, fetchInvoces]);

  const accountDateTime = currentAccount ? { timezone: currentAccount.timezone.name, time: accountDatetime } : null;

  let userDateTime;
  if (currentAccount && currentUser) {
    const userTimezone = currentUser.timezone || currentAccount.timezone;
    userDateTime = { timezone: userTimezone.name, time: userDatetime };
  }

  const isNewFeatureScopeActive = hasNewFeatureScope(currentAccount);
  const isNewBilling = currentAccount?.new_billing || false;

  const showOldPricingPlanTile = !isNewFeatureScopeActive && !isNewBilling;

  const salesforceOrderIsNotEmpty =
    currentAccount?.salesforce_order && Object.keys(currentAccount.salesforce_order).length > 0;
  const currentChargeIsNotEmpty = currentCharge && Object.keys(currentCharge).length > 0;
  const showNewPricingPlanTile = isNewBilling && salesforceOrderIsNotEmpty && currentChargeIsNotEmpty;

  return (
    <div className="account-data-grid">
      {showOldPricingPlanTile ? (
        <div className="account-data-card--main">
          <PricingPlanTile
            currentCharge={currentCharge}
            currentAccount={currentAccount}
            isLoading={isLoadingPaymentMethod || isChargeDataLoading}
          />
        </div>
      ) : null}
      {showNewPricingPlanTile ? (
        <div className="account-data-card--main">
          <NewPricingPlanTile
            currentCharge={currentCharge}
            currentAccount={currentAccount}
            isLoading={isLoadingPaymentMethod || isChargeDataLoading}
          />
        </div>
      ) : null}
      <div className="account-data-card--main">
        <InvoicesTile
          isNewFeatureScopeActive={isNewFeatureScopeActive}
          isNewBilling={isNewBilling}
          currentCharge={currentCharge}
          invoices={invoices}
          href={hrefList.invoices}
          isLoading={isChargeDataLoading}
          extraClasses={classNames({
            'your-data-card--muted': !currentCharge && !invoices,
          })}
        />
      </div>
      <div className="account-data-card--main">
        <UsersTile rolesCount={rolesCount} totalUsers={totalUsers} href={hrefList.users} isLoading={isUsersLoading} />
      </div>
      <div className="account-data-card--main">
        <PartnerAdAccountsTile
          partnerAdAccountsCount={partnerAdAccountsCount}
          href={hrefList.partnerAdAccounts}
          isLoading={isPartnerAdAccountsLoading}
        />
      </div>
      <div className="account-data-card--main">
        <AccountHistoryTile href={hrefList.accountHistory} isLoading={isEventsLoading} events={events} />
      </div>
      {currentAccount?.payment_method === 'credit card' && (
        <div className="account-data-card--side">
          <CreditCardTile
            currency={currency}
            paymentMethod={paymentMethod}
            href={hrefList.paymentMethod}
            isLoading={isLoadingPaymentMethod}
            extraClasses={classNames({
              'your-data-card--muted': !paymentMethod,
            })}
          />
        </div>
      )}

      <div className="account-data-card--side">
        <AccountDetailsTile
          account={currentAccount}
          dateTime={accountDateTime}
          href={hrefList.companyDetails}
          isLoading={!accountDateTime}
        />
      </div>

      {hasSecurityPackage && (
        <div className="account-data-card--side">
          <SecurityConfigurationTile
            ssoHref={hrefList.ssoConfiguration}
            twoFactorHref={hrefList.twoFactorConfiguration}
            twoFactorEnabled={twoFactorConfigurationEnabled}
            isLoading={isTwoFactorLoading}
          />
        </div>
      )}

      <div className="account-data-card--side account-data-card--double">
        <UserDetailsTile
          user={currentUser}
          dateTime={userDateTime}
          href={hrefList.userDetails}
          isLoading={!userDateTime}
        />
      </div>
    </div>
  );
};
